import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMangoApi } from '~/atoms/MangoApi'
import {
  AgentCustomStatus,
  AgentCustomStatusRequest,
} from '~/types/AgentCustomStatus'

import { ApiError } from '@mangovoice/types'

export const useGetCustomStatuses = (pbxId?: number) => {
  const api = useMangoApi()

  return useQuery<AgentCustomStatus[], ApiError>({
    queryKey: ['customStatuses', pbxId],
    queryFn: async () => {
      if (!pbxId) return []
      const response = await api.getCustomStatuses({ pbx_id: pbxId })
      if (!response.ok) {
        throw new Error(response.problem || 'Failed to fetch custom statuses')
      }
      return response?.data?.results ?? []
    },
    enabled: !!pbxId,
  })
}

export const useCreateCustomStatus = (
  onSuccess?: (data: AgentCustomStatus) => void,
) => {
  const api = useMangoApi()

  return useMutation({
    mutationFn: async (data: AgentCustomStatusRequest) => {
      const response = await api.postCustomStatus(data)
      if (!response.ok) {
        throw new Error(response.problem || 'Failed to create custom status')
      }
      if (!response.data) {
        throw new Error('No data received')
      }
      return response.data
    },
    onSuccess: (data) => {
      onSuccess?.(data)
    },
  })
}

export const useUpdateCustomStatus = (
  onSuccess?: (updatedStatus: AgentCustomStatus) => void,
) => {
  const api = useMangoApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      statusId,
      data,
    }: {
      statusId: number
      data: AgentCustomStatusRequest
    }) => {
      const response = await api.putCustomStatus(statusId, data)
      if (!response.ok) {
        throw new Error(response.problem || 'Failed to update custom status')
      }
      if (!response.data) {
        throw new Error('No data received')
      }
      return response.data
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['customStatuses'] })
      onSuccess?.(data)
    },
  })
}

export const useDeleteCustomStatus = (onSuccess?: () => void) => {
  const api = useMangoApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (statusId: number) => {
      const response = await api.deleteCustomStatus(statusId)
      if (!response.ok) {
        throw new Error(response.problem || 'Failed to delete custom status')
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customStatuses'] })
      onSuccess?.()
    },
  })
}
